"use client";

import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getSupabase } from "@/lib/hooks/api/utils";
import type { Email_Template_Favorites } from "@/src/gql/graphql";

const FAVORITES_QUERY_KEY = ["email-template-favorites"] as const;

type FavoriteRow = Pick<Email_Template_Favorites, "template_id">;

async function fetchFavoriteIds(): Promise<string[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from("email_template_favorites")
    .select("template_id")
    .order("created_at", { ascending: false });
  if (error) throw new Error(error.message);
  const rows: FavoriteRow[] = data ?? [];
  return rows.map((r) => r.template_id);
}

async function addFavorite(templateId: string): Promise<void> {
  const supabase = getSupabase();
  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser();
  if (authError || !user) throw new Error("User not authenticated");

  // Idempotent: ignore the unique-constraint conflict if it already exists.
  const { error } = await supabase
    .from("email_template_favorites")
    .upsert(
      { user_id: user.id, template_id: templateId },
      { onConflict: "user_id,template_id", ignoreDuplicates: true }
    );
  if (error) throw new Error(error.message);
}

async function removeFavorite(templateId: string): Promise<void> {
  const supabase = getSupabase();
  // RLS restricts the delete to the caller's own rows.
  const { error } = await supabase
    .from("email_template_favorites")
    .delete()
    .eq("template_id", templateId);
  if (error) throw new Error(error.message);
}

/**
 * Per-user email-template favorites, persisted in the `email_template_favorites`
 * table (cross-device). Shared by the compose-time TemplateSelector and the
 * Email Templates management table. The return shape is intentionally identical
 * to the previous localStorage implementation so callers need no changes.
 */
export function useEmailTemplateFavorites() {
  const queryClient = useQueryClient();

  const {
    data: favoriteIds = [],
    isFetched,
  } = useQuery({
    queryKey: FAVORITES_QUERY_KEY,
    queryFn: fetchFavoriteIds,
    staleTime: 60_000,
  });

  const favoriteIdSet = useMemo(() => new Set(favoriteIds), [favoriteIds]);

  const mutation = useMutation({
    mutationFn: async (templateId: string) => {
      const isCurrentlyFavorite = favoriteIdSet.has(templateId);
      if (isCurrentlyFavorite) {
        await removeFavorite(templateId);
      } else {
        await addFavorite(templateId);
      }
    },
    // Optimistic toggle so the star flips instantly.
    onMutate: async (templateId: string) => {
      await queryClient.cancelQueries({ queryKey: FAVORITES_QUERY_KEY });
      const previous = queryClient.getQueryData<string[]>(FAVORITES_QUERY_KEY) ?? [];
      const next = previous.includes(templateId)
        ? previous.filter((id) => id !== templateId)
        : [templateId, ...previous];
      queryClient.setQueryData<string[]>(FAVORITES_QUERY_KEY, next);
      return { previous };
    },
    onError: (_error, _templateId, context) => {
      if (context?.previous) {
        queryClient.setQueryData<string[]>(FAVORITES_QUERY_KEY, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: FAVORITES_QUERY_KEY });
    },
  });

  const isFavorite = useCallback((id: string) => favoriteIdSet.has(id), [favoriteIdSet]);

  const toggleFavorite = useCallback(
    (id: string) => {
      mutation.mutate(id);
    },
    [mutation]
  );

  /**
   * No-op retained for API compatibility. Orphaned favorites are impossible now:
   * the row's template_id FK cascades on template delete.
   */
  const pruneMissing = useCallback((_validIds: ReadonlySet<string>) => {}, []);

  return {
    favoriteIds,
    favoriteIdSet,
    isFavorite,
    toggleFavorite,
    pruneMissing,
    hydrated: isFetched,
  };
}
